import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2, X, CheckCircle2 } from "lucide-react";
import { api, apiError } from "@/lib/api";

const STATUS = {
  will_update: ["Will mark shipped", "text-primary"],
  updated: ["Marked shipped", "text-green-600"],
  unchanged: ["Already shipped", "text-muted-foreground"],
  no_tracking: ["No tracking number in file", "text-amber-600"],
  not_found: ["Order not found", "text-destructive"],
};

// Upload a Royal Mail Click & Drop results/despatch export; preview, then apply tracking + shipped.
export const ShipmentImport = ({ onApplied }) => {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const send = async (f, apply) => {
    const fd = new FormData();
    fd.append("file", f);
    const { data } = await api.post(`/admin/orders/import-shipments${apply ? "?apply=true" : ""}`, fd);
    return data;
  };

  const pick = async (f) => {
    if (!f) return;
    setFile(f); setBusy(true);
    try { setPreview(await send(f, false)); } catch (e) { toast.error(apiError(e)); setFile(null); } finally { setBusy(false); }
  };

  const apply = async () => {
    setBusy(true);
    try {
      const data = await send(file, true);
      setPreview(data);
      toast.success(`${data.summary.updated} order${data.summary.updated === 1 ? "" : "s"} marked shipped`);
      onApplied?.();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const close = () => { setPreview(null); setFile(null); if (fileRef.current) fileRef.current.value = ""; };

  return (
    <>
      <input ref={fileRef} type="file" accept=".csv,.xlsx,.xlsm,.txt" className="hidden" data-testid="shipments-file-input" onChange={(e) => pick(e.target.files?.[0])} />
      <button data-testid="shipments-import-btn" onClick={() => fileRef.current?.click()} disabled={busy}
        className="inline-flex items-center gap-1.5 border border-border px-4 py-2 text-xs font-medium hover:border-primary disabled:opacity-60">
        {busy && !preview ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Import Click &amp; Drop results
      </button>

      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="shipments-preview">
          <div className="max-h-[90vh] w-full max-w-3xl overflow-hidden border border-border bg-background">
            <div className="flex items-center justify-between border-b border-border bg-muted px-5 py-3">
              <div>
                <div className="font-heading text-base font-bold">{preview.applied ? "Shipments updated" : "Review before applying"}</div>
                <div className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{file?.name}</div>
              </div>
              <button onClick={close} data-testid="shipments-close" className="p-1 hover:bg-background"><X className="h-4 w-4" /></button>
            </div>
            <div className="mono flex flex-wrap gap-2 px-5 py-3 text-[11px]" data-testid="shipments-summary">
              {Object.entries(preview.summary).filter(([, n]) => n > 0).map(([k, n]) => (
                <span key={k} className={`border border-border px-2 py-1 ${STATUS[k]?.[1] || ""}`}>{STATUS[k]?.[0] || k}: <b>{n}</b></span>
              ))}
            </div>
            <div className="max-h-[55vh] overflow-y-auto px-5">
              <table className="w-full text-sm">
                <thead className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                  <tr><th className="py-2 text-left">Order</th><th className="text-left">Tracking</th><th className="text-left">Currently</th><th className="text-left">Result</th></tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {preview.rows.map((r) => (
                    <tr key={r.order_number} data-testid={`shipments-row-${r.order_number}`}>
                      <td className="py-2 mono text-xs">{r.order_number}</td>
                      <td className="mono text-xs">{r.tracking_number || "—"}</td>
                      <td className="text-xs text-muted-foreground">{r.current ? `${r.current}${r.current_tracking ? ` · ${r.current_tracking}` : ""}` : "—"}</td>
                      <td className={`text-xs font-medium ${STATUS[r.status]?.[1] || ""}`}>{STATUS[r.status]?.[0] || r.status}{r.status === "updated" && r.email_sent ? " · email sent" : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between gap-3 border-t border-border px-5 py-3">
              <p className="text-[11px] text-muted-foreground">Orders marked shipped get their tracking number saved and the customer's "shipped" email (where an email exists). Orders that already carry the same tracking are left alone.</p>
              {preview.applied ? (
                <button onClick={close} data-testid="shipments-done" className="inline-flex items-center gap-1.5 bg-foreground px-4 py-2 text-xs font-medium text-background"><CheckCircle2 className="h-3.5 w-3.5" /> Done</button>
              ) : (
                <button onClick={apply} disabled={busy || !preview.summary.will_update} data-testid="shipments-apply"
                  className="inline-flex items-center gap-1.5 bg-primary px-4 py-2 text-xs font-medium text-primary-foreground disabled:opacity-50">
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />} Mark {preview.summary.will_update} as shipped
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
